import { DashboardTaskGroup, GetTaskGroupParams, GetTaskGroupResponse } from '@lmc/ts-common'
import { useInfiniteQuery } from 'react-query'
import { getQueryParams } from 'utils/getQueryParams'
import { TaskFiltersValue } from '../components/TaskFilters/TaskFilters'
import { convertPropsToParams } from './utils'
import { getJSON } from 'common/services/api'
import { useState } from 'react'

export const GET_TASK_GROUPS = `/api/list/tasks/dashboard/taskgroup`
export type TaskGroupCursor = {
  date: string,
  recurringTaskID: string,
}

export type UseTaskGroupProps = Omit<TaskFiltersValue, 'form' | 'to'> & {
  initialCursor: TaskGroupCursor | undefined,
}

const useTaskGroups = (props: UseTaskGroupProps) => {
  const [taskGroups, setTaskGroups] = useState<DashboardTaskGroup[]>([])
  const [enabled, setEnabled] = useState(false)

  const { isLoading,
    refetch,
    fetchNextPage,
    hasNextPage = true,
    isFetchingNextPage 
  } = useInfiniteQuery<{ result: GetTaskGroupResponse }>({
    queryKey: ['task-group', props],
    queryFn: ({ pageParam }) => {
      let cursor = pageParam
      if (pageParam === undefined) {
        cursor = props.initialCursor
      }
      const convertedProps = convertTaskGroupPropsToTaskGroupParams({
        ...props,
        cursor,
      })

      const queryParams = getQueryParams(convertedProps)

      return  getJSON({
        url: queryParams
          ? `${GET_TASK_GROUPS}?${queryParams}`
          : GET_TASK_GROUPS,
      })
    },
    getNextPageParam: (lastPage): TaskGroupCursor | undefined => {
      if (lastPage.result.pagination.hasNextPage === false) {
        return undefined
      }

      const lastTaskGroupIndex = lastPage.result?.taskGroups.length
      const lastTaskGroup = lastPage.result?.taskGroups[lastTaskGroupIndex - 1]

      const cursor = {
        date: lastTaskGroup.dateTime,
        recurringTaskID: lastTaskGroup.recurringTaskID,
      }
      
      return cursor
    },
    onSuccess: (data) => {
      let taskGroups: DashboardTaskGroup[] = []

      data?.pages.forEach((page) => {
        if (page.result?.taskGroups) {
          taskGroups = taskGroups.concat(page.result.taskGroups)
        }
      })

      setTaskGroups(taskGroups)
    },
    enabled,
    keepPreviousData: true,
  })


  const customFetchNextPage = () => {
    if (!enabled) {
      setEnabled(true)
    }

    fetchNextPage()
  }

  const customRefetch = async () => {
    if (enabled) {
      await refetch()
    }
  }

  return {
    taskGroups,
    isLoading, 
    refetch: customRefetch, 
    fetchNextPage: customFetchNextPage, 
    hasNextPage,
    isFetchingNextPage,
  }
}

export const convertTaskGroupPropsToTaskGroupParams = (props: Omit<UseTaskGroupProps, 'initialCursor'> & { cursor: TaskGroupCursor } | undefined): GetTaskGroupParams => {
  const baseParams = convertPropsToParams(props)

  return {
    ...baseParams,
    pagination: props?.cursor && {
      cursorDate: props.cursor.date,
      cursorRecurringTaskID: props.cursor.recurringTaskID,
    }
  }
}

export default useTaskGroups